import React, { useState, useEffect } from 'react';
import { Typography, Space, Button, Card, Form, Input, Breadcrumb, Row, Col, Spin, Image, Alert, Modal } from 'antd';
import { EyeOutlined, FileImageOutlined, DeleteOutlined, CloseCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { tenantService, Tenant } from '../services/tenants';
import noticeService from '../services/notices';
import { NoticeItem } from '../types/notice';
import { showConfirmModal } from '../components/PopupModal';
import authService from '../services/auth';
import TenantPreviewModal from '../components/TenantPreviewModal';
import NoticePreviewModal from '../components/NoticePreviewModal';
import { message } from '../utils/message';

const { Title, Text } = Typography;
const { TextArea } = Input;

type ContentType = 'tenant' | 'notice';

const ContentApprovalDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const contentType = (searchParams.get('type') || 'tenant') as ContentType;

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [notice, setNotice] = useState<NoticeItem | null>(null);
  const [isEventContent, setIsEventContent] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState<string>('');
  const [rejectionReason, setRejectionReason] = useState<string>('');
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [isPreviewModalVisible, setIsPreviewModalVisible] = useState(false);

  useEffect(() => {
    if (id) {
      loadContentData();
    }
  }, [id, contentType]);

  const getLayoutDisplayName = (layout: string) => {
    if (contentType === 'tenant') {
      switch (layout) {
        case 'layout1':
          return '레이아웃 1 (5개 구성)';
        case 'layout2':
          return '레이아웃 2 (4개 구성)';
        default:
          return '-';
      }
    } else {
      return layout === '1' ? '1칸' : '2칸';
    }
  };

  const getApprovalStatusText = (status: string): string => {
    switch (status) {
      case 'pending':
        return '승인대기';
      case 'approved':
        return '승인완료';
      case 'rejected':
        return '승인거부';
      default:
        return status;
    }
  };

  const formatPhoneNumber = (phone: string): string => {
    if (!phone) return '-';
    const numbers = phone.replace(/[^0-9]/g, '');
    if (numbers.startsWith('02')) {
      if (numbers.length === 9) {
        return numbers.replace(/(\d{2})(\d{3})(\d{4})/, '$1-$2-$3');
      } else if (numbers.length === 10) {
        return numbers.replace(/(\d{2})(\d{4})(\d{4})/, '$1-$2-$3');
      }
    } else if (numbers.startsWith('010') || numbers.startsWith('011') || numbers.startsWith('016') || numbers.startsWith('017') || numbers.startsWith('018') || numbers.startsWith('019')) {
      if (numbers.length === 11) {
        return numbers.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
      }
    } else {
      if (numbers.length === 10) {
        return numbers.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
      } else if (numbers.length === 11) {
        return numbers.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
      }
    }
    return phone;
  };

  const renderApprovalAlert = () => {
    if (!approvalStatus) return null;

    let alertType: 'info' | 'error' | 'warning' = 'info';
    let alertMessage = '';
    let icon = <InfoCircleOutlined />;

    const isSuperAdmin = authService.isSuperAdmin();

    switch (approvalStatus) {
      case 'pending':
        if (isSuperAdmin) {
          alertMessage = '승인이 필요한 콘텐츠입니다. 검토 후 승인 여부를 진행해주세요.';
          alertType = 'info';
        } else {
          alertMessage = '승인 대기중인 콘텐츠입니다. 수정 및 삭제가 불가합니다.';
          alertType = 'warning';
        }
        icon = <InfoCircleOutlined />;
        break;
      case 'rejected':
        alertType = 'error';
        alertMessage = rejectionReason
          ? `승인 거부된 콘텐츠입니다.\n사유 : ${rejectionReason}`
          : '승인 거부된 콘텐츠입니다.';
        icon = <CloseCircleOutlined />;
        break;
      case 'approved':
        return null;
      default:
        return null;
    }

    return (
      <Alert
        message={alertMessage}
        type={alertType}
        icon={icon}
        showIcon
        style={{
          marginBottom: '24px',
          borderRadius: '8px',
          padding: '16px 20px',
          whiteSpace: 'pre-line'
        }}
      />
    );
  };

  const loadContentData = async () => {
    try {
      setLoading(true);
      if (contentType === 'tenant') {
        const response = await tenantService.getTenant(Number(id));
        if (response.success) {
          setTenant(response.data);
          setApprovalStatus(response.data.approvalStatus);
          setRejectionReason(response.data.rejectionReason || '');
          const hasEventContent = response.data.isEventContent || false;
          setIsEventContent(hasEventContent);

          form.setFieldsValue({
            companyName: response.data.name,
            representative: response.data.phone || '',
            content: response.data.description || '',
            eventBrand: hasEventContent ? 'Y' : 'N',
            exposure: response.data.status === 'active' ? '노출' : '비노출',
            layoutName: getLayoutDisplayName(response.data.layout),
            registrationDate: response.data.createdAt ? new Date(response.data.createdAt).toLocaleDateString('ko-KR').replace(/\. /g, '.').replace(/\.$/, '') : '-',
            approvalDate: response.data.updatedAt ? new Date(response.data.updatedAt).toLocaleDateString('ko-KR').replace(/\. /g, '.').replace(/\.$/, '') : '-',
            lastUpdateDate: response.data.updatedAt ? new Date(response.data.updatedAt).toLocaleDateString('ko-KR').replace(/\. /g, '.').replace(/\.$/, '') : '-',
            approvalStatus: getApprovalStatusText(response.data.approvalStatus)
          });
        }
      } else {
        const noticeData = await noticeService.getNoticeDetail(Number(id));
        setNotice(noticeData);
        setApprovalStatus(noticeData.approvalStatus);
        setRejectionReason(noticeData.rejectionReason || '');

        form.setFieldsValue({
          title: noticeData.title,
          layoutName: getLayoutDisplayName(noticeData.layout),
          exposure: noticeData.exposure === 'show' ? '노출' : '비노출',
          startDate: noticeData.startDate ? new Date(noticeData.startDate).toLocaleDateString('ko-KR').replace(/\. /g, '.').replace(/\.$/, '') : '-',
          endDate: noticeData.endDate ? new Date(noticeData.endDate).toLocaleDateString('ko-KR').replace(/\. /g, '.').replace(/\.$/, '') : '-',
          additionalInfo: noticeData.additionalInfo || '',
          registrationDate: noticeData.createdAt ? new Date(noticeData.createdAt).toLocaleDateString('ko-KR').replace(/\. /g, '.').replace(/\.$/, '') : '-',
          approvalDate: noticeData.approvedAt ? new Date(noticeData.approvedAt).toLocaleDateString('ko-KR').replace(/\. /g, '.').replace(/\.$/, '') : '-',
          lastUpdateDate: noticeData.updatedAt ? new Date(noticeData.updatedAt).toLocaleDateString('ko-KR').replace(/\. /g, '.').replace(/\.$/, '') : '-',
          approvalStatus: getApprovalStatusText(noticeData.approvalStatus)
        });
      }
    } catch (error) {
      console.error('Failed to load content data:', error);
      message.error('데이터를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handlePrevious = () => {
    navigate('/content-approval');
  };

  const handleEdit = () => {
    navigate(`/content-approval/edit/${id}?type=${contentType}`);
  };

  const handleDelete = async () => {
    showConfirmModal({
      title: '알림',
      content: `삭제한 콘텐츠는 복구할 수 없습니다.\n삭제하시겠습니까?`,
      okText: '확인',
      okType: 'danger',
      onConfirm: async () => {
        try {
          if (contentType === 'tenant') {
            await tenantService.deleteTenant(Number(id));
            message.success('삭제가 완료되었습니다.');
          } else {
            await noticeService.deleteNotice(Number(id));
            message.success('삭제가 완료되었습니다.');
          }
          navigate('/content-approval');
        } catch (error) {
          console.error('Delete error:', error);
          message.error('삭제에 실패했습니다.');
        }
      },
    });
  };

  const handleApproval = async (approvalStatus: 'approved' | 'rejected') => {
    if (approvalStatus === 'rejected') {
      // 승인거부는 모달 표시
      setRejectModalVisible(true);
      return;
    }

    // 승인완료 처리
    const statusText = '승인완료';

    showConfirmModal({
      title: `${statusText} 처리`,
      content: `${statusText} 처리하시겠습니까?`,
      okText: statusText,
      okType: 'primary',
      onConfirm: async () => {
        try {
          if (contentType === 'tenant') {
            await tenantService.approveTenant(Number(id), approvalStatus);
          } else {
            await noticeService.approveNotice(Number(id), approvalStatus);
          }
          message.success("승인 되었습니다.");
          loadContentData();
        } catch (error) {
          console.error('Approval error:', error);
          message.error('승인 처리에 실패했습니다.');
        }
      },
    });
  };

  const handleRejectConfirm = async () => {
    if (!rejectReason.trim()) {
      message.warning('사유를 입력해주세요.');
      return;
    }

    try {
      if (contentType === 'tenant') {
        await tenantService.approveTenant(Number(id), 'rejected', rejectReason);
      } else {
        await noticeService.approveNotice(Number(id), 'rejected', rejectReason);
      }
      message.success('승인 거부 되었습니다.');
      setRejectModalVisible(false);
      setRejectReason('');
      loadContentData();
    } catch (error) {
      console.error('Reject error:', error);
      message.error('승인 거부 처리에 실패했습니다.');
    }
  };

  const handleRejectCancel = () => {
    setRejectModalVisible(false);
    setRejectReason('');
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* 브레드크럼 */}
      <Breadcrumb
        items={[
          { title: '콘텐츠 관리' },
          { title: '콘텐츠 승인 관리', href: '/content-approval' },
          { title: contentType === 'tenant' ? '입주사 소개 상세' : '공지사항 상세' }
        ]}
        style={{ marginBottom: '8px' }}
      />

      {/* 페이지 제목 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={2} style={{ margin: 0 }}>
          {contentType === 'tenant'
            ? `입주사 소개 상세${tenant?.name ? ` - ${tenant.name}` : ''}`
            : `공지사항 상세${notice?.title ? ` - ${notice.title}` : ''}`
          }
        </Title>
        <Button onClick={() => setIsPreviewModalVisible(true)}>
          미리보기
        </Button>
      </div>

      <Form form={form} layout="vertical" style={{ width: '100%' }}>
        {/* 승인 상태 메시지 */}
        {renderApprovalAlert()}

        {/* 기본 정보 */}
        <Card title="기본 정보" style={{ marginBottom: '24px' }}>
          <Row gutter={[24, 16]}>
            <Col span={6}>
              <Form.Item label={<span style={{ fontWeight: 'bold' }}>레이아웃명</span>} name="layoutName">
                <Text>{form.getFieldValue('layoutName') || '-'}</Text>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[24, 16]} style={contentType !== 'notice' ? { height: '54px' } : {}}>
            <Col span={6}>
              <Form.Item label={<span style={{ fontWeight: 'bold' }}>노출여부</span>} name="exposure">
                <Text>{form.getFieldValue('exposure') || '-'}</Text>
              </Form.Item>
            </Col>
          </Row>
          {contentType === 'notice' && (
            <Row gutter={[24, 16]} style={{ height: '54px' }}>
              <Col span={12}>
                <Form.Item label={<span style={{ fontWeight: 'bold' }}>노출기간</span>}>
                  <Text>
                    {form.getFieldValue('startDate') && form.getFieldValue('endDate')
                      ? `${form.getFieldValue('startDate')} ~ ${form.getFieldValue('endDate')}`
                      : '-'
                    }
                  </Text>
                </Form.Item>
              </Col>
            </Row>
          )}
        </Card>

        {/* 입주사 정보 또는 공지사항 정보 */}
        {contentType === 'tenant' ? (
          <>
            <Card title="입주사 정보" style={{ marginBottom: '24px' }}>
              <Row gutter={[24, 16]}>
                <Col span={12}>
                  <Form.Item label={<span style={{ fontWeight: 'bold' }}>입주사명</span>} name="companyName">
                    <Text>{form.getFieldValue('companyName') || '-'}</Text>
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={[24, 16]} style={{ height: '54px' }}>
                <Col span={12}>
                  <Form.Item label={<span style={{ fontWeight: 'bold' }}>대표번호</span>} name="representative">
                    <Text>{formatPhoneNumber(form.getFieldValue('representative'))}</Text>
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            <Card title="입주사 설명" style={{ marginBottom: '24px', height: '100%' }}>
              <Form.Item label={<span style={{ fontWeight: 'bold' }}>내용</span>} name="content">
                <Text style={{ whiteSpace: 'pre-line' }}>{form.getFieldValue('content') || '-'}</Text>
              </Form.Item>
            </Card>

            <Card title="입주사 이미지" style={{ marginBottom: '24px' }}>
              <div>
                <Text style={{ display: 'block', marginBottom: '16px', color: '#ffffff', fontWeight: 'bold' }}>첨부파일</Text>
                {tenant?.imageUrl ? (
                  <div style={{ border: '1px solid #303030', borderRadius: '8px', padding: '16px', backgroundColor: '#1a1a1a' }}>
                    <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                      <Image
                        width={120}
                        height={90}
                        src={tenant.imageUrl}
                        alt="입주사 이미지"
                        style={{ objectFit: 'cover', borderRadius: '4px' }}
                        preview={{ mask: <div><EyeOutlined /> 미리보기</div> }}
                      />
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                          <FileImageOutlined style={{ color: '#F24822' }} />
                          <Text style={{ color: '#ffffff' }}>입주사 대표 이미지</Text>
                        </div>
                        <Text style={{ color: '#a6a6a6', fontSize: '12px' }}>이미지가 등록되어 있습니다.</Text>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={{ border: '1px dashed #303030', borderRadius: '8px', padding: '32px', textAlign: 'center', backgroundColor: '#1a1a1a' }}>
                    <FileImageOutlined style={{ fontSize: '48px', color: '#666666', marginBottom: '16px' }} />
                    <Text style={{ color: '#a6a6a6', display: 'block' }}>등록된 이미지가 없습니다.</Text>
                  </div>
                )}
              </div>
            </Card>

            <Card title="기타 정보" style={{ marginBottom: '24px' }}>
              <Row gutter={[24, 16]}>
                <Col span={24}>
                  <Form.Item label={<span style={{ fontWeight: 'bold' }}>이벤트 콘텐츠</span>} name="eventBrand">
                    <Text>{form.getFieldValue('eventBrand') || '-'}</Text>
                  </Form.Item>

                  {isEventContent && (
                    <div style={{ marginTop: '24px' }}>
                      <Text style={{ display: 'block', marginBottom: '16px', color: '#ffffff' }}>이벤트 첨부파일</Text>
                      {tenant?.thumbnailUrl ? (
                        <div style={{ border: '1px solid #303030', borderRadius: '8px', padding: '16px', backgroundColor: '#1a1a1a' }}>
                          <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                            <Image
                              width={120}
                              height={90}
                              src={tenant.thumbnailUrl}
                              alt="이벤트 이미지"
                              style={{ objectFit: 'cover', borderRadius: '4px' }}
                              preview={{ mask: <div><EyeOutlined /> 미리보기</div> }}
                            />
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                <FileImageOutlined style={{ color: '#F24822' }} />
                                <Text style={{ color: '#ffffff' }}>이벤트 이미지</Text>
                              </div>
                              <Text style={{ color: '#a6a6a6', fontSize: '12px' }}>이벤트 이미지가 등록되어 있습니다.</Text>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div style={{ border: '1px dashed #303030', borderRadius: '8px', padding: '32px', textAlign: 'center', backgroundColor: '#1a1a1a' }}>
                          <FileImageOutlined style={{ fontSize: '48px', color: '#666666', marginBottom: '16px' }} />
                          <Text style={{ color: '#a6a6a6', display: 'block' }}>등록된 이벤트 이미지가 없습니다.</Text>
                        </div>
                      )}
                    </div>
                  )}
                </Col>
              </Row>
            </Card>
          </>
        ) : (
          <>
            <Card title="상세 내용" style={{ marginBottom: '24px' }}>
              <div style={{ marginBottom: '24px' }}>
                <Form.Item label={<span style={{ fontWeight: 'bold' }}>제목</span>} name="title">
                  <Text>{form.getFieldValue('title') || '-'}</Text>
                </Form.Item>
              </div>
              <div>
                <Text style={{ color: '#a6a6a6', display: 'block', marginBottom: '12px', fontWeight: 'bold' }}>내용</Text>
                {notice?.contents && Array.isArray(notice.contents) ? notice.contents.map((content, index) => (
                  <div key={index} style={{ marginBottom: index < notice.contents.length - 1 ? '16px' : 0, padding: '16px', background: '#1a1a1a', borderRadius: '8px' }}>
                    <div style={{ marginBottom: '8px' }}>
                      <Text style={{ fontWeight: '500', color: '#ffffff' }}>{content.subtitle || '제목 없음'}</Text>
                    </div>
                    <Text style={{ color: '#d9d9d9', lineHeight: '1.6' }}>{content.detail || '내용 없음'}</Text>
                  </div>
                )) : (
                  <div style={{ padding: '16px', background: '#1a1a1a', borderRadius: '8px' }}>
                    <Text style={{ color: '#d9d9d9' }}>공지사항 내용이 없습니다.</Text>
                  </div>
                )}
              </div>
            </Card>

            <Card title="기타 정보" style={{ marginBottom: '24px' }}>
              <Form.Item label={<span style={{ fontWeight: 'bold' }}>추가 안내</span>} name="additionalInfo">
                <Text style={{ whiteSpace: 'pre-line' }}>{form.getFieldValue('additionalInfo') || '-'}</Text>
              </Form.Item>
            </Card>
          </>
        )}

        {/* 승인 정보 */}
        <Card title="승인 정보" style={{ marginBottom: '32px' }}>
          <Row gutter={[24, 16]}>
            <Col span={6}>
              <Form.Item label={<span style={{ fontWeight: 'bold' }}>등록일</span>} name="registrationDate">
                <Text>{form.getFieldValue('registrationDate') || '-'}</Text>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[24, 16]}>
            <Col span={6}>
              <Form.Item label={<span style={{ fontWeight: 'bold' }}>승인일</span>} name="approvalDate">
                <Text>{form.getFieldValue('approvalDate') || '-'}</Text>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[24, 16]}  style={{ height: '54px' }}>
            <Col span={6}>
              <Form.Item label={<span style={{ fontWeight: 'bold' }}>최근 수정일</span>} name="lastUpdateDate">
                <Text>{form.getFieldValue('lastUpdateDate') || '-'}</Text>
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* 액션 버튼 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
          <Button
            danger
            size="large"
            icon={<DeleteOutlined />}
            onClick={handleDelete}
            // style={{ height: 48 }}
          >
            삭제
          </Button>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button
              size="large"
              onClick={handlePrevious}
              // style={{ height: 48 }}
            >
              이전
            </Button>
            {approvalStatus === 'pending' && (
              <>
                <Button
                  size="large"
                  onClick={handleEdit}
                  // style={{ height: 48 }}
                >
                  수정
                </Button>
                <Button
                  danger
                  size="large"
                  onClick={() => handleApproval('rejected')}
                  // style={{ height: 48 }}
                >
                  승인거부
                </Button>
                <Button
                  type="primary"
                  size="large"
                  onClick={() => handleApproval('approved')}
                  // style={{ height: 48 }}
                >
                  승인완료
                </Button>
              </>
            )}
            {approvalStatus === 'approved' && (
              <Button
                size="large"
                onClick={handleEdit}
                // style={{ height: 48 }}
              >
                수정
              </Button>
            )}
          </div>
        </div>
      </Form>

      {/* 승인 거부 모달 */}
      <Modal
        title="승인 거부"
        open={rejectModalVisible}
        onOk={handleRejectConfirm}
        onCancel={handleRejectCancel}
        okText="확인"
        cancelText="취소"
        centered
        width={600}
        destroyOnHidden={true}
        maskClosable={false}
        okButtonProps={{
          size: 'large',
          style: {
            minWidth: 100,
            // height: 48,
            fontSize: '16px',
            fontWeight: 500,
            backgroundColor: '#F24822',
            color: '#ffffff',
            borderColor: '#F24822'
          }
        }}
        cancelButtonProps={{
          size: 'large',
          style: {
            minWidth: 100,
            height: 48,
            fontSize: '16px',
            fontWeight: 500
          }
        }}
        styles={{
          header: {
            paddingBottom: 16,
            marginBottom: 24,
            borderBottom: '1px solid #303030'
          },
          body: {
            paddingTop: 0
          },
          footer: {
            marginTop: 32,
            paddingTop: 24,
            borderTop: '1px solid #303030'
          }
        }}
      >
        <div>
          <div style={{ marginBottom: '8px' }}>
            <span style={{ fontSize: '14px', fontWeight: 500 }}>
              사유 <span style={{ color: '#ff4d4f' }}>*</span>
            </span>
          </div>
          <Input.TextArea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="사유를 입력하세요"
            maxLength={30}
            showCount
            rows={4}
            style={{
              resize: 'none',
              fontSize: '14px'
            }}
          />
        </div>
      </Modal>

      {/* 미리보기 모달 */}
      {contentType === 'tenant' ? (
        <TenantPreviewModal
          visible={isPreviewModalVisible}
          onClose={() => setIsPreviewModalVisible(false)}
          tenant={tenant}
        />
      ) : (
        <NoticePreviewModal
          visible={isPreviewModalVisible}
          onClose={() => setIsPreviewModalVisible(false)}
          notice={notice}
        />
      )}
    </Space>
  );
};

export default ContentApprovalDetail;
